import { Link } from "react-router-dom";
import { topics } from "@/mocks/topics";
import AttitudeBar from "@/components/base/AttitudeBar";
import SectionHeading from "@/components/base/SectionHeading";
import { formatNumber } from "@/utils/format";

const totalWeight = topics.reduce((sum, t) => sum + t.poll.totalVotes, 0);
const weighted = topics.reduce(
  (acc, t) => ({
    support: acc.support + t.attitude.support * t.poll.totalVotes,
    neutral: acc.neutral + t.attitude.neutral * t.poll.totalVotes,
    oppose: acc.oppose + t.attitude.oppose * t.poll.totalVotes,
  }),
  { support: 0, neutral: 0, oppose: 0 }
);

const aggregate = {
  support: Math.round(weighted.support / totalWeight),
  neutral: Math.round(weighted.neutral / totalWeight),
  oppose: Math.round(weighted.oppose / totalWeight),
};

const leaning = aggregate.support >= aggregate.oppose ? "支持" : "反对";
const leaningPercent = Math.max(aggregate.support, aggregate.oppose);

export default function AttitudeBoard() {
  return (
    <section className="border-y border-background-200 bg-background-100">
      <div className="mx-auto w-full px-4 py-12 md:px-8 md:py-16">
        <SectionHeading
          eyebrow="舆论态度分布"
          title="全网态度一眼看清"
          subtitle="把每个话题的投票结果折算成态度占比，支持、中立与反对三段并列展示，避免只看到情绪最激烈的那一面。"
          linkTo="/polls"
          linkLabel="看完整投票风向"
        />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <p className="font-label text-xs tracking-[0.18em] text-foreground-500 uppercase">
              整体风向
            </p>
            <p className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950">
              当前更偏向
              <span className="text-primary-600">{leaning}</span>
              一侧
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              综合 {topics.length} 个话题、{formatNumber(totalWeight)} 份投票折算，
              {leaning}一方占比约 {leaningPercent}%，中立意见仍是不可忽视的第三极。
            </p>
            <div className="mt-5">
              <AttitudeBar attitude={aggregate} />
            </div>
            <div className="mt-5 rounded-md bg-secondary-50 p-4">
              <p className="font-label text-xs text-secondary-800">
                态度分布只反映参与投票者的选择，不代表全体网友的判断。
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {topics.map((topic) => (
                <article
                  key={topic.id}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-heading text-sm font-bold leading-snug text-foreground-950">
                      <Link
                        to={`/topics/${topic.id}`}
                        className="cursor-pointer transition-colors hover:text-primary-600"
                      >
                        {topic.title}
                      </Link>
                    </h3>
                    <span className="shrink-0 rounded-full bg-background-100 px-2.5 py-1 font-label text-xs text-foreground-600">
                      {topic.category}
                    </span>
                  </div>
                  <div className="mt-4">
                    <AttitudeBar attitude={topic.attitude} size="sm" showLegend={false} />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 font-label text-xs text-foreground-500">
                    <span className="text-secondary-700">支持 {topic.attitude.support}%</span>
                    <span>中立 {topic.attitude.neutral}%</span>
                    <span className="text-primary-700">反对 {topic.attitude.oppose}%</span>
                    <span className="ml-auto">{formatNumber(topic.poll.totalVotes)} 票</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}